// Sorting the book table.
//
// The payload arrives in a deliberate order: untouched accounts first, then by credit burn,
// so the highest-signal unworked account is at the top. That worklist is the point of this
// view, not an arbitrary default, so it stays reachable -- clicking a column cycles
// descending, ascending, then back to the worklist rather than trapping the reader in a sort
// they cannot undo.
//
// Rows may also carry a numeric `group`. After the column sort, lower groups stay above
// higher ones, and the chosen sort applies within each group — in all three cycle states.
// Pin / body / booked / hide uses 0 / 1 / 2 / 3. GRO-138's available table should set
// the same field.

import type { BookColumn } from './columns';
import type { RankedAccount } from './derived';

export interface SortState {
  key: string | null;
  direction: 1 | -1;
}

export const WORKLIST_ORDER: SortState = { key: null, direction: -1 };

// Rank 1 is the work to do first, so this column's interesting end is the top of
// an ascending sort rather than the bottom of a descending one.
export const RANK_ORDER: SortState = { key: 'rank', direction: 1 };

// Descending first on every column except rank. A third click returns to `fallback`,
// which is rank order when a ranking is live and the worklist otherwise.
export function cycleSort(
  current: SortState,
  key: string,
  fallback: SortState = WORKLIST_ORDER,
): SortState {
  const firstDirection: 1 | -1 = key === 'rank' ? 1 : -1;
  if (current.key !== key) return { key, direction: firstDirection };
  if (current.direction === firstDirection) {
    return { key, direction: firstDirection === 1 ? -1 : 1 };
  }
  return fallback;
}

// Missing values sort last in both directions. "Never touched" is not "touched a very long
// time ago", and an account with no playground org has no credit figure at all -- letting
// either drift to the top of an ascending sort would read as a real zero. Ties break on
// name so repeated renders do not reshuffle equal rows.
export function compareSortValues(
  left: string | number | null,
  right: string | number | null,
  direction: 1 | -1,
  leftName: string,
  rightName: string,
): number {
  if (left == null && right == null) return leftName.localeCompare(rightName);
  if (left == null) return 1;
  if (right == null) return -1;

  const comparison = typeof left === 'string' && typeof right === 'string'
    ? left.localeCompare(right)
    : Number(left) - Number(right);

  return comparison ? comparison * direction : leftName.localeCompare(rightName);
}

export function sortBy<T>(
  rows: T[],
  sort: SortState,
  value: (row: T) => string | number | null,
  name: (row: T) => string,
): T[] {
  if (!sort.key) return rows;
  return rows.slice().sort((first, second) => (
    compareSortValues(value(first), value(second), sort.direction, name(first), name(second))
  ));
}

function sortByColumn(
  rows: RankedAccount[],
  sort: SortState,
  columns: BookColumn[],
): RankedAccount[] {
  if (!sort.key) return rows;
  const column = columns.find(candidate => candidate.key === sort.key);
  if (!column) return rows;
  return sortBy(rows, sort, row => column.sortValue(row), row => row.account);
}

function sortByGroup(rows: RankedAccount[]): RankedAccount[] {
  return rows.slice().sort((first, second) => {
    const firstGroup = first.group ?? 0;
    const secondGroup = second.group ?? 0;
    return firstGroup - secondGroup;
  });
}

export function sortRows(
  rows: RankedAccount[],
  sort: SortState,
  columns: BookColumn[],
): RankedAccount[] {
  // Column order first, then group. Array.sort is stable, so each group keeps the
  // column (or worklist) order. A pin that vanished on sort would not be a pin.
  return sortByGroup(sortByColumn(rows, sort, columns));
}
